/**
 * Body Map Alteration Component - SuiOS
 * Interactive garment diagram for recording alterations
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  RotateCcw, Save, Plus, Trash2, Clock, IndianRupee, ListChecks, MapPin,
  Loader2, Eye, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { GARMENT_ZONES, GarmentZone, getAlterationTypesForZone, getGarmentViewBox } from './BodyMapZones';
import { settingsAPI } from '@/lib/api';

const ALTERATION_TYPE_LABELS = {
  shorten: 'Shorten',
  lengthen: 'Lengthen',
  take_in: 'Take In',
  let_out: 'Let Out',
  reshape: 'Reshape',
  reposition: 'Reposition',
  narrow: 'Narrow',
  widen: 'Widen',
};

export default function BodyMapAlteration({
  garmentType = 'jacket',
  garmentLabel = 'Jacket',
  existingAlterations = [],
  completedAlterations = [],
  onSave,
  onCancel,
  readOnly = false,
}) {
  const [view, setView] = useState('front'); // 'front' or 'back'
  const [selectedZone, setSelectedZone] = useState(null);
  const [alterations, setAlterations] = useState(existingAlterations);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [timeMatrix, setTimeMatrix] = useState({});
  const [chargesMatrix, setChargesMatrix] = useState({});
  const [saving, setSaving] = useState(false);
  const [simpleMode, setSimpleMode] = useState(false);
  
  // Form state for zone popup
  const [formData, setFormData] = useState({
    zoneId: '',
    zoneLabel: '',
    alterationType: '',
    adjustment: 0,
    notes: '',
    estimatedHours: 0,
  });

  // Fetch time and charges matrices
  useEffect(() => {
    const fetchMatrices = async () => {
      try {
        const [timeRes, chargesRes] = await Promise.all([
          settingsAPI.get('alteration-time-matrix'),
          settingsAPI.get('alteration-charges'),
        ]);
        setTimeMatrix(timeRes.data?.value || {});
        setChargesMatrix(chargesRes.data?.value || {});
      } catch (err) {
        console.error('Failed to load alteration matrices:', err);
      }
    };
    fetchMatrices();
  }, []);

  // Get zones for current garment and view
  const getZones = useCallback(() => {
    const garmentZones = GARMENT_ZONES[garmentType] || GARMENT_ZONES.jacket;
    return garmentZones[view] || garmentZones.front || [];
  }, [garmentType, view]);

  // Check if a zone has an alteration marked
  const getZoneAlteration = useCallback((zoneId) => {
    return alterations.find(a => a.zoneId === zoneId);
  }, [alterations]);

  // Check if a zone has a completed alteration
  const isZoneCompleted = useCallback((zoneId) => {
    return completedAlterations.some(a => a.zoneId === zoneId || a.garment_area === zoneId);
  }, [completedAlterations]);

  // Get estimated hours for an alteration
  const getEstimatedHours = useCallback((garmentType, zoneId, alterationType) => {
    const gt = garmentType.toLowerCase();
    const garmentMatrix = timeMatrix[gt] || {};
    
    // Map zone to area
    let area = zoneId;
    if (zoneId.includes('sleeve')) area = 'sleeve';
    if (zoneId.includes('hem')) area = 'hem';
    if (zoneId.includes('waist')) area = 'waist';
    if (zoneId.includes('chest')) area = 'body';
    if (zoneId.includes('shoulder')) area = 'shoulder';
    if (zoneId.includes('collar')) area = 'collar';
    if (zoneId.includes('seat') || zoneId.includes('crotch')) area = 'seat';
    
    return garmentMatrix[area] || 1;
  }, [timeMatrix]);

  // Get charge for an alteration
  const getCharge = useCallback((garmentType, zoneId, alterationType) => {
    const gt = garmentType.toLowerCase();
    const garmentCharges = chargesMatrix[gt] || {};
    
    // Map zone to area
    let area = zoneId;
    if (zoneId.includes('sleeve')) area = 'sleeve';
    if (zoneId.includes('hem')) area = 'hem';
    if (zoneId.includes('waist')) area = 'waist';
    if (zoneId.includes('chest') || zoneId.includes('body')) area = 'body';
    if (zoneId.includes('shoulder')) area = 'shoulder';
    if (zoneId.includes('collar')) area = 'collar';
    if (zoneId.includes('seat')) area = 'seat';
    
    const areaCharges = garmentCharges[area];
    if (!areaCharges) return 500; // Default
    if (typeof areaCharges === 'number') return areaCharges;
    return areaCharges[alterationType] || 500;
  }, [chargesMatrix]);

  // Handle zone click
  const handleZoneClick = (zone) => {
    if (readOnly) return;
    
    const existingAlt = getZoneAlteration(zone.id);
    const estHours = getEstimatedHours(garmentType, zone.id, 'take_in');
    
    setFormData({
      zoneId: zone.id,
      zoneLabel: zone.label,
      alterationType: existingAlt?.alterationType || '',
      adjustment: existingAlt?.adjustment || 0,
      notes: existingAlt?.notes || '',
      estimatedHours: existingAlt?.estimatedHours || estHours,
    });
    setSelectedZone(zone);
    setSheetOpen(true);
  };

  // Update estimated hours when alteration type changes
  const handleAlterationTypeChange = (type) => {
    const estHours = getEstimatedHours(garmentType, formData.zoneId, type);
    setFormData(p => ({
      ...p,
      alterationType: type,
      estimatedHours: estHours,
    }));
  };

  // Add or update alteration
  const handleAddAlteration = () => {
    if (!formData.alterationType) {
      toast.error('Please select an alteration type');
      return;
    }
    
    const charge = getCharge(garmentType, formData.zoneId, formData.alterationType);
    const alteration = {
      id: `alt-${formData.zoneId}-${Date.now()}`,
      zoneId: formData.zoneId,
      zoneLabel: formData.zoneLabel,
      alterationType: formData.alterationType,
      adjustment: formData.adjustment,
      notes: formData.notes,
      estimatedHours: formData.estimatedHours,
      estimatedCost: charge,
      garmentType,
      garmentLabel,
    };
    
    // Update or add
    setAlterations(prev => {
      const existing = prev.findIndex(a => a.zoneId === formData.zoneId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = alteration;
        return updated;
      }
      return [...prev, alteration];
    });
    
    toast.success(`${formData.zoneLabel} alteration added`);
    setSheetOpen(false);
    setSelectedZone(null);
  };

  // Remove alteration
  const handleRemoveAlteration = (zoneId) => {
    setAlterations(prev => prev.filter(a => a.zoneId !== zoneId));
    toast.success('Alteration removed');
    setSheetOpen(false);
    setSelectedZone(null);
  };

  // Calculate totals
  const totalHours = alterations.reduce((sum, a) => sum + (a.estimatedHours || 0), 0);
  const totalCost = alterations.reduce((sum, a) => sum + (a.estimatedCost || 0), 0);

  // Save all alterations
  const handleSaveAll = async () => {
    if (alterations.length === 0) {
      toast.error('No alterations to save');
      return;
    }
    setSaving(true);
    try {
      await onSave?.(alterations);
      toast.success('Alterations saved');
    } catch (err) {
      toast.error('Failed to save alterations');
    } finally {
      setSaving(false);
    }
  };

  // Check if garment has back view
  const hasBackView = GARMENT_ZONES[garmentType]?.back?.length > 0;
  const zones = getZones();
  const viewBox = getGarmentViewBox(garmentType);

  return (
    <div className="space-y-4" data-testid="body-map-alteration">
      {/* Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Switch
            checked={simpleMode}
            onCheckedChange={setSimpleMode}
            id="simple-mode"
          />
          <Label htmlFor="simple-mode" className="text-xs cursor-pointer" style={{ color: 'var(--ink-muted)' }}>
            {simpleMode ? 'Simple View' : 'Body Map View'}
          </Label>
        </div>
        {hasBackView && !simpleMode && (
          <div className="flex items-center gap-2">
            <Button
              variant={view === 'front' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('front')}
              className="text-xs"
              style={view === 'front' ? { background: 'var(--brand-navy)' } : {}}
            >
              Front
            </Button>
            <Button
              variant={view === 'back' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('back')}
              className="text-xs"
              style={view === 'back' ? { background: 'var(--brand-navy)' } : {}}
            >
              Back
            </Button>
          </div>
        )}
      </div>

      {simpleMode ? (
        /* Simple Text-Based View */
        <SimpleAlterationView
          garmentType={garmentType}
          garmentLabel={garmentLabel}
          alterations={alterations}
          setAlterations={setAlterations}
          timeMatrix={timeMatrix}
          chargesMatrix={chargesMatrix}
          readOnly={readOnly}
        />
      ) : (
        /* Body Map View */
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <MapPin className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
              {garmentLabel} - {view === 'front' ? 'Front View' : 'Back View'}
            </CardTitle>
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
              Tap a zone to mark an alteration
            </p>
          </CardHeader>
          <CardContent className="flex justify-center py-4">
            <svg
              viewBox={viewBox}
              className="w-full max-w-md"
              style={{ minHeight: '500px', maxHeight: '600px' }}
              data-testid="body-map-svg"
            >
              {/* Garment outline */}
              <rect x="0" y="0" width="400" height={parseInt(viewBox.split(' ')[3])} fill="transparent" />
              
              {/* Render zones */}
              {zones.map(zone => {
                const zoneAlt = getZoneAlteration(zone.id);
                const completed = isZoneCompleted(zone.id);
                return (
                  <GarmentZone
                    key={zone.id}
                    zone={zone}
                    isSelected={selectedZone?.id === zone.id}
                    isMarked={!!zoneAlt}
                    isCompleted={completed}
                    adjustment={zoneAlt?.adjustment}
                    onClick={handleZoneClick}
                    disabled={readOnly}
                  />
                );
              })}
              
              {/* Zone labels on hover would be nice, but keeping it simple */}
            </svg>
          </CardContent>
        </Card>
      )}

      {/* Marked Alterations Summary */}
      {alterations.length > 0 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <ListChecks className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
              Marked Alterations ({alterations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alterations.map(alt => (
              <div
                key={alt.id}
                className="flex items-center justify-between p-2 rounded-lg border border-border/50 hover:border-border"
                data-testid={`alteration-item-${alt.zoneId}`}
              >
                <div className="flex-1">
                  <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                    {alt.zoneLabel}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-xs" style={{
                      background: 'rgba(197,165,90,0.12)',
                      color: 'var(--brand-navy)',
                      borderColor: 'transparent',
                    }}>
                      {ALTERATION_TYPE_LABELS[alt.alterationType] || alt.alterationType}
                    </Badge>
                    {alt.adjustment !== 0 && (
                      <Badge variant="outline" className="text-xs" style={{
                        background: alt.adjustment < 0 ? 'rgba(239,68,68,0.10)' : 'rgba(34,197,94,0.10)',
                        color: alt.adjustment < 0 ? '#EF4444' : '#22C55E',
                        borderColor: 'transparent',
                      }}>
                        {alt.adjustment > 0 ? '+' : ''}{alt.adjustment}cm
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-xs">
                    <div className="flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                      <Clock className="w-3 h-3" /> {alt.estimatedHours}h
                    </div>
                    <div className="font-medium" style={{ color: '#1E7A5F' }}>
                      ₹{alt.estimatedCost?.toLocaleString('en-IN')}
                    </div>
                  </div>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAlteration(alt.zoneId)}
                      className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {/* Totals */}
            <div className="flex justify-between pt-3 mt-3 border-t">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                  <Clock className="w-4 h-4" /> Total: {totalHours}h
                </span>
              </div>
              <span className="text-lg font-bold" style={{ color: '#1E7A5F' }}>
                ₹{totalCost.toLocaleString('en-IN')}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {!readOnly && (
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAll}
            disabled={saving || alterations.length === 0}
            className="gap-2"
            style={{ background: 'var(--brand-navy)' }}
            data-testid="save-alterations-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save All Alterations
          </Button>
        </div>
      )}

      {/* Zone Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-[400px] bg-white" data-testid="zone-edit-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>
              {selectedZone?.label}
            </SheetTitle>
            <SheetDescription>
              Specify alteration details for this zone
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs">Alteration Type</Label>
              <Select
                value={formData.alterationType}
                onValueChange={handleAlterationTypeChange}
              >
                <SelectTrigger className="mt-1" data-testid="alteration-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {getAlterationTypesForZone(formData.zoneId).map(type => (
                    <SelectItem key={type} value={type}>
                      {ALTERATION_TYPE_LABELS[type] || type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs">Adjustment (cm)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(p => ({ ...p, adjustment: p.adjustment - 0.5 }))}
                  className="h-9 w-9 p-0"
                >
                  -
                </Button>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.adjustment}
                  onChange={e => setFormData(p => ({ ...p, adjustment: parseFloat(e.target.value) || 0 }))}
                  className="text-center"
                  data-testid="adjustment-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(p => ({ ...p, adjustment: p.adjustment + 0.5 }))}
                  className="h-9 w-9 p-0"
                >
                  +
                </Button>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                Negative = reduce, Positive = add
              </p>
            </div>
            
            <div>
              <Label className="text-xs">Notes / Instructions</Label>
              <Textarea
                value={formData.notes}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="e.g., Match with left sleeve..."
                className="mt-1"
                rows={2}
                data-testid="notes-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 p-3 rounded-lg" style={{ background: 'var(--surface-2)' }}>
              <div>
                <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Est. Hours</Label>
                <div className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>
                  {formData.estimatedHours}h
                </div>
              </div>
              <div>
                <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Est. Cost</Label>
                <div className="text-lg font-semibold" style={{ color: '#1E7A5F' }}>
                  ₹{getCharge(garmentType, formData.zoneId, formData.alterationType).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          </div>
          <SheetFooter className="gap-2">
            {getZoneAlteration(formData.zoneId) && (
              <Button
                variant="outline"
                onClick={() => handleRemoveAlteration(formData.zoneId)}
                className="text-red-500 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
            <Button
              onClick={handleAddAlteration}
              className="gap-1.5"
              style={{ background: 'var(--brand-navy)' }}
              data-testid="add-alteration-btn"
            >
              <Plus className="w-4 h-4" />
              {getZoneAlteration(formData.zoneId) ? 'Update' : 'Add'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ============================================================
   Simple Text-Based Alteration View (Fallback)
   ============================================================ */
function SimpleAlterationView({
  garmentType,
  garmentLabel,
  alterations,
  setAlterations,
  timeMatrix,
  chargesMatrix,
  readOnly,
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({
    area: '',
    alterationType: '',
    adjustment: 0,
    notes: '',
  });

  const zones = GARMENT_ZONES[garmentType]?.front || [];
  const allAreas = zones.map(z => ({ value: z.id, label: z.label }));

  const getEstHours = (area) => {
    const gt = garmentType.toLowerCase();
    const garmentMatrix = timeMatrix[gt] || {};
    let mapped = area;
    if (area.includes('sleeve')) mapped = 'sleeve';
    if (area.includes('hem')) mapped = 'hem';
    if (area.includes('waist')) mapped = 'waist';
    return garmentMatrix[mapped] || 1;
  };

  const getCharge = (area, type) => {
    const gt = garmentType.toLowerCase();
    const garmentCharges = chargesMatrix[gt] || {};
    let mapped = area;
    if (area.includes('sleeve')) mapped = 'sleeve';
    if (area.includes('hem')) mapped = 'hem';
    if (area.includes('waist')) mapped = 'waist';
    const areaCharges = garmentCharges[mapped];
    if (!areaCharges) return 500;
    if (typeof areaCharges === 'number') return areaCharges;
    return areaCharges[type] || 500;
  };

  const handleAdd = () => {
    if (!form.area || !form.alterationType) {
      toast.error('Please select area and alteration type');
      return;
    }
    const zone = zones.find(z => z.id === form.area);
    const alteration = {
      id: `alt-${form.area}-${Date.now()}`,
      zoneId: form.area,
      zoneLabel: zone?.label || form.area,
      alterationType: form.alterationType,
      adjustment: form.adjustment,
      notes: form.notes,
      estimatedHours: getEstHours(form.area),
      estimatedCost: getCharge(form.area, form.alterationType),
      garmentType,
      garmentLabel,
    };
    setAlterations(prev => [...prev, alteration]);
    setForm({ area: '', alterationType: '', adjustment: 0, notes: '' });
    setFormOpen(false);
    toast.success('Alteration added');
  };

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>
          {garmentLabel} Alterations
        </CardTitle>
        {!readOnly && (
          <Button size="sm" onClick={() => setFormOpen(true)} className="gap-1 text-xs" style={{ background: 'var(--brand-navy)' }}>
            <Plus className="w-3 h-3" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {alterations.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: 'var(--ink-muted)' }}>
            No alterations marked
          </p>
        ) : (
          <div className="space-y-2">
            {alterations.map(alt => (
              <div key={alt.id} className="flex justify-between items-center p-2 border rounded-lg">
                <div>
                  <span className="text-sm font-medium">{alt.zoneLabel}</span>
                  <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                    {ALTERATION_TYPE_LABELS[alt.alterationType]} {alt.adjustment !== 0 && `(${alt.adjustment > 0 ? '+' : ''}${alt.adjustment}cm)`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs">{alt.estimatedHours}h • ₹{alt.estimatedCost}</span>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAlterations(prev => prev.filter(a => a.id !== alt.id))}
                      className="text-red-500 h-6 w-6 p-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Simple Add Form */}
        {formOpen && (
          <div className="mt-4 p-3 border rounded-lg space-y-3" style={{ background: 'var(--surface-2)' }}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Area</Label>
                <Select value={form.area} onValueChange={v => setForm(p => ({ ...p, area: v }))}>
                  <SelectTrigger className="mt-1 text-xs h-8">
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    {allAreas.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.alterationType} onValueChange={v => setForm(p => ({ ...p, alterationType: v }))}>
                  <SelectTrigger className="mt-1 text-xs h-8">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ALTERATION_TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Adjustment (cm)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.adjustment}
                  onChange={e => setForm(p => ({ ...p, adjustment: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 h-8 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input
                  value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  className="mt-1 h-8 text-xs"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAdd} style={{ background: 'var(--brand-navy)' }}>Add</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
